import { Button, Flex } from "@mantine/core";
import clsx from "clsx";
import { useCallback, useEffect, useRef } from "react";
import type { InputOption } from "@/types";
import {
  translateIdentity,
  type StaticTextTranslator,
} from "@/utils/staticTextTranslator";
import styles from "./YesNo.module.css";

interface YesNoProps {
  /** Callback when user selects an option */
  onSubmit: (value: InputOption) => void;
  /** Optional custom labels for Yes/No options */
  options?: InputOption[];
  /** Whether the buttons are disabled */
  disabled?: boolean;
  /** Whether to use the compact local benchmark appearance */
  isBenchmark?: boolean;
  initialSelectedOptionId?: string;
  /** Optional translation function for fallback Yes/No labels */
  translate?: StaticTextTranslator;
}

/**
 * Yes/No button pair for binary choice questions
 */
export const YesNo = ({
  onSubmit,
  options = [],
  disabled = false,
  isBenchmark = false,
  initialSelectedOptionId,
  translate = translateIdentity,
}: YesNoProps) => {
  const hasSubmittedRef = useRef(false);
  const wasDisabledRef = useRef(disabled);

  useEffect(() => {
    if (wasDisabledRef.current && !disabled) {
      hasSubmittedRef.current = false;
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  const yesOption: InputOption = options[0] || {
    id: "yes",
    optionText: translate("Yes"),
    optionValue: "yes",
    sortOrder: 0,
  };
  const noOption: InputOption = options[1] || {
    id: "no",
    optionText: translate("No"),
    optionValue: "no",
    sortOrder: 1,
  };

  const handleClick = useCallback(
    (value: InputOption) => () => {
      if (!disabled && !hasSubmittedRef.current) {
        hasSubmittedRef.current = true;
        try {
          onSubmit(value);
        } catch (error) {
          hasSubmittedRef.current = false;
          throw error;
        }
      }
    },
    [disabled, onSubmit],
  );

  return (
    <Flex
      align="flex-end"
      className={isBenchmark ? styles.benchmarkOptions : undefined}
      data-variant={isBenchmark ? "benchmark" : undefined}
      direction={isBenchmark ? "row" : "column"}
      style={
        isBenchmark ? { flexDirection: "row", flexWrap: "wrap" } : undefined
      }
      wrap={isBenchmark ? "wrap" : "nowrap"}
    >
      <Button
        aria-pressed={initialSelectedOptionId === yesOption.id}
        className={clsx(
          styles.yesNoButton,
          initialSelectedOptionId === yesOption.id && styles.selected,
        )}
        onClick={handleClick(yesOption)}
        disabled={disabled}
        unstyled
      >
        {translate(yesOption.optionText)}
      </Button>
      <Button
        aria-pressed={initialSelectedOptionId === noOption.id}
        className={clsx(
          styles.yesNoButton,
          initialSelectedOptionId === noOption.id && styles.selected,
        )}
        onClick={handleClick(noOption)}
        disabled={disabled}
        unstyled
      >
        {translate(noOption.optionText)}
      </Button>
    </Flex>
  );
};
